import { useFetchCatalogoVisitasTraslados } from "@/shared/actions/erp/administration/catalogo-visitas-traslados.actions";

/** Slug del Parámetro (key-value Django) que guarda el catálogo de conceptos. */
export const SLUG_PRECIOS_CONCEPTOS_VISITA = "precios_conceptos_visita";

/**
 * Flujo al que aplica un concepto / método de pago / config de puntos de red.
 * El catálogo es UNO solo (mismo Parametro) pero cada opción se liga de forma
 * independiente a visitas, a traslados o a ambos desde Catálogos > Administración.
 * Los registros viejos (sin `aplica_a`) se consideran de VISITA.
 */
export type FlujoConcepto = "VISITA" | "TRASLADO";
export const FLUJOS_CONCEPTO: FlujoConcepto[] = ["VISITA", "TRASLADO"];
export const FLUJO_LABEL: Record<FlujoConcepto, string> = {
  VISITA: "Visitas",
  TRASLADO: "Traslados",
};

/** Normaliza un `aplica_a` crudo a una lista válida (default: solo VISITA). */
export function normalizarAplicaA(raw: unknown): FlujoConcepto[] {
  const list = Array.isArray(raw) ? raw : [];
  const out = list
    .map((f) => String(f || "").trim().toUpperCase())
    .filter((f): f is FlujoConcepto => (FLUJOS_CONCEPTO as string[]).includes(f));
  const unicos = out.filter((f, i, a) => a.indexOf(f) === i);
  return unicos.length > 0 ? unicos : ["VISITA"];
}

/** IVA por defecto (%). Parametrizable desde Catálogos > Administración. */
export const DEFAULT_IVA_PORCENTAJE = 15;

/**
 * Config por defecto de "Puntos de Red": cobro del CABLE UTP por metro. Los
 * primeros `metros_libres` no se cobran; el excedente se cobra por metro o
 * fracción (ceil). Precio sin IVA por metro (de la tabla: CABLE UTP 0.58).
 * Parametrizable desde Catálogos > Administración.
 */
export const DEFAULT_PUNTO_RED_PRECIO_METRO = 0.58;
export const DEFAULT_PUNTO_RED_METROS_LIBRES = 15;

/**
 * Métodos de pago de conceptos de visita por defecto. Son PARAMETRIZABLES desde
 * Catálogos > Administración (se pueden borrar o agregar más). Se muestran en el
 * selector "Método de pago" de "Otros conceptos" en todas las ventanas.
 */
export const METODOS_PAGO_VISITA_DEFAULT: string[] = [
  "EFECTIVO",
  "FACTURA",
  "COMODATO",
  "Fibrapuntos",
  "Retenciones",
];

export type ConfigPuntoRed = {
  precio_metro_sin_iva: number;
  metros_libres: number;
};

/**
 * Config de "Excedente de fibra" (solo TRASLADOS): los primeros `metros_gratis`
 * no se cobran; lo que los supere se cobra por metro o fracción al
 * `precio_metro_sin_iva`. Parametrizable desde Catálogos > Administración >
 * Traslados. Arranca con costo $0: Activaciones gestiona el precio al aprobar.
 */
export const DEFAULT_EXCEDENTE_FIBRA_METROS_GRATIS = 600;
export const DEFAULT_EXCEDENTE_FIBRA_PRECIO_METRO = 0;

export type ConfigExcedenteFibra = {
  metros_gratis: number;
  precio_metro_sin_iva: number;
};

/**
 * Cálculo REFERENCIAL del excedente de fibra (vista previa en pantalla). El
 * valor que se guarda y se cobra lo calcula el backend con la misma regla.
 */
export function calcularExcedenteFibra(
  metros: number,
  cfg: ConfigExcedenteFibra,
  ivaPorcentaje: number
): PrecioConcepto & { metros_cobrados: number } {
  const gratis = Math.max(0, Number(cfg.metros_gratis) || 0);
  const m = Math.max(0, Number(metros) || 0);
  const metros_cobrados = Math.ceil(Math.max(0, m - gratis) - 1e-9);
  const precio = calcularPrecio(metros_cobrados * (Number(cfg.precio_metro_sin_iva) || 0), ivaPorcentaje);
  return { ...precio, metros_cobrados };
}

/**
 * Calcula el cobro de un punto de red aislado (compat). Primeros `metros_libres`
 * gratis; excedente = ceil(metros - libres). Para el cálculo real de una visita
 * usar `calcularCobrosPuntosRed` (los metros libres son GLOBALES sobre el total).
 */
export function calcularCobroPuntoRed(
  metros: number,
  cfg: ConfigPuntoRed,
  ivaPorcentaje: number
): PrecioConcepto & { metros_cobrados: number } {
  const libres = Number(cfg.metros_libres) || 0;
  const m = Number(metros) || 0;
  const excedente = m > libres ? Math.ceil(m - libres) : 0;
  const precio = calcularPrecio(excedente * (Number(cfg.precio_metro_sin_iva) || 0), ivaPorcentaje);
  return { ...precio, metros_cobrados: excedente };
}

/**
 * Calcula el cobro de TODOS los puntos de red de una visita. Los `metros_libres`
 * se aplican POR CONCEPTO ligado: cada concepto (con `permite_punto_red`) tiene
 * sus propios metros libres (ej. 15m); si tiene varios puntos, se suman y el
 * umbral aplica al total de ESE concepto. Los puntos SIN concepto ligado NO
 * tienen metros libres (se cobra todo).
 *
 * Ej.: Router Wifi 5 → 16m (cobra 1m); Concepto B → 10m (0m, no excede 15);
 * punto libre → 8m (cobra 8m). El excedente se reparte por "waterfall" dentro de
 * cada grupo (concepto), acumulando punto a punto, con redondeo a metro o
 * fracción (ceil) sobre el acumulado del grupo. Devuelve un cálculo por punto
 * (mismo orden que el input) para enviar a Softnet de forma individual.
 */
export function calcularCobrosPuntosRed(
  puntos: Array<{ metros?: number; concepto?: string }>,
  cfg: ConfigPuntoRed,
  ivaPorcentaje: number
): Array<PrecioConcepto & { metros_cobrados: number }> {
  const libres = Number(cfg.metros_libres) || 0;
  const precioMetro = Number(cfg.precio_metro_sin_iva) || 0;
  // Acumulado de metros por GRUPO. Grupo = nombre del concepto ligado, o
  // "__sin_concepto__" para los puntos libres (esos no tienen metros gratis).
  const acumPorGrupo: Record<string, number> = {};
  return (Array.isArray(puntos) ? puntos : []).map((p) => {
    const m = Number(p?.metros) || 0;
    const concepto = p?.concepto ? String(p.concepto).trim() : "";
    const grupoKey = concepto || "__sin_concepto__";
    const grupoLibres = concepto ? libres : 0; // sin concepto → cobra todo
    const antes = acumPorGrupo[grupoKey] || 0;
    const acum = antes + m;
    acumPorGrupo[grupoKey] = acum;
    const excAntes = Math.ceil(Math.max(0, antes - grupoLibres));
    const excHasta = Math.ceil(Math.max(0, acum - grupoLibres));
    const metros_cobrados = Math.max(0, excHasta - excAntes);
    const precio = calcularPrecio(metros_cobrados * precioMetro, ivaPorcentaje);
    return { ...precio, metros_cobrados };
  });
}

export type PrecioConcepto = {
  valor_sin_iva: number;
  iva: number;
  valor_total: number;
};

/** Producto de inventario ligado a un concepto. */
export type ProductoConcepto = {
  codigo?: string;
  id_producto?: string;
  descripcion?: string;
};

/**
 * Un concepto del catálogo: nombre + precio + (opcional) UNO O VARIOS productos de
 * inventario relacionados. Es la ÚNICA fuente para el selector de "Otros conceptos"
 * en todas las vistas. Se administra desde Catálogos > Administración.
 *
 * `liga_producto`: si false, el concepto NO se relaciona con inventario (no se
 * elige producto ni serie, no se egresa stock) — p.ej. "Visita facturada" y
 * "Punto de red". Si true, `productos` tiene 1..N productos; el técnico elige cuál
 * entregó y su serie.
 *
 * `aplica_a`: flujos donde el concepto está disponible (VISITA y/o TRASLADO). Se
 * configura por concepto; los registros viejos sin el campo son de VISITA.
 */
export type ConceptoCatalogo = {
  concepto: string;
  valor_sin_iva: number;
  iva: number;
  valor_total: number;
  liga_producto: boolean;
  productos: ProductoConcepto[];
  /**
   * Si true, a este concepto se le pueden LIGAR puntos de red (cada concepto con
   * red tiene sus propios metros libres). Se configura en Catálogos > Administración.
   */
  permite_punto_red: boolean;
  aplica_a: FlujoConcepto[];
  // Compat: primer producto (código único) para código que aún lee estos campos.
  codigo?: string;
  id_producto?: string;
  descripcion_producto?: string;
};

/** Conceptos que por defecto NO se ligan a un producto de inventario. */
export const CONCEPTOS_SIN_PRODUCTO_DEFAULT = ["Punto de red", "Visita facturada"];

/** Etiquetas por defecto (semilla inicial del catálogo). */
export const CONCEPTOS_VISITA_OPCIONES: string[] = [
  "ONT",
  "ANTENA",
  "Router Wifi 5",
  "Router Wifi 6",
  "Fuente de alimentación",
  "Mini UPS",
  "Punto de red",
  "Visita facturada",
  "Cámara",
];

/** Valor base (sin IVA) por defecto por concepto (de la tabla entregada). */
export const DEFAULT_VALOR_SIN_IVA: Record<string, number> = {
  ONT: 46,
  ANTENA: 0,
  "Router Wifi 5": 41.4,
  "Router Wifi 6": 56.35,
  "Fuente de alimentación": 5.75,
  "Mini UPS": 72,
  "Punto de red": 0,
  "Visita facturada": 0,
  "Cámara": 0,
};

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Calcula IVA y Valor total a partir del valor sin IVA y el % de IVA.
 * IVA = valor_sin_iva * (% / 100); Total = valor_sin_iva + IVA. Redondeo 2 dec.
 */
export function calcularPrecio(
  valorSinIva: number,
  ivaPorcentaje: number
): PrecioConcepto {
  const sin = round2(valorSinIva);
  const iva = round2(sin * ((Number(ivaPorcentaje) || 0) / 100));
  return { valor_sin_iva: sin, iva, valor_total: round2(sin + iva) };
}

/** Semilla por defecto del catálogo (array de conceptos, sin producto ligado). */
export function conceptosPorDefecto(ivaPorcentaje: number): ConceptoCatalogo[] {
  return CONCEPTOS_VISITA_OPCIONES.map((c) => ({
    concepto: c,
    ...calcularPrecio(DEFAULT_VALOR_SIN_IVA[c] ?? 0, ivaPorcentaje),
    liga_producto: !CONCEPTOS_SIN_PRODUCTO_DEFAULT.includes(c),
    permite_punto_red: false,
    productos: [],
    aplica_a: ["VISITA"],
  }));
}

/** Normaliza un array de conceptos crudos calculando IVA/total + productos[]. */
export function normalizarConceptos(
  raw: any[] | null | undefined,
  ivaPorcentaje: number
): ConceptoCatalogo[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return conceptosPorDefecto(ivaPorcentaje);
  }
  return raw
    .filter((c) => c && typeof c === "object" && c.concepto)
    .map((c) => {
      // Productos: nuevo formato `productos[]`; o migrar el `codigo` único viejo.
      let productos: ProductoConcepto[] = [];
      if (Array.isArray(c.productos)) {
        productos = c.productos
          .filter((p: any) => p && (p.codigo || p.id_producto))
          .map((p: any) => ({
            codigo: p.codigo || undefined,
            id_producto: p.id_producto || undefined,
            descripcion: p.descripcion || p.descripcion_producto || undefined,
          }));
      } else if (c.codigo || c.id_producto) {
        productos = [{
          codigo: c.codigo || undefined,
          id_producto: c.id_producto || undefined,
          descripcion: c.descripcion_producto || undefined,
        }];
      }
      // liga_producto: explícito; si no viene, se infiere (tiene productos, y no
      // está en la lista de no-ligados por defecto).
      const ligaProducto =
        typeof c.liga_producto === "boolean"
          ? c.liga_producto
          : productos.length > 0 &&
            !CONCEPTOS_SIN_PRODUCTO_DEFAULT.includes(String(c.concepto));
      const primero = productos[0] || {};
      return {
        concepto: String(c.concepto),
        ...calcularPrecio(Number(c.valor_sin_iva) || 0, ivaPorcentaje),
        liga_producto: ligaProducto,
        permite_punto_red: !!c.permite_punto_red,
        productos,
        aplica_a: normalizarAplicaA(c.aplica_a),
        // Compat con lecturas antiguas (primer producto).
        codigo: primero.codigo,
        id_producto: primero.id_producto,
        descripcion_producto: primero.descripcion,
      };
    });
}

/** Lee una config de puntos de red cruda; si falta algo usa el fallback. */
function parsearPuntoRed(raw: any, fallback: ConfigPuntoRed): ConfigPuntoRed {
  const precioMetroRaw = Number(raw?.precio_metro_sin_iva);
  const metrosLibresRaw = Number(raw?.metros_libres);
  return {
    precio_metro_sin_iva: Number.isFinite(precioMetroRaw)
      ? precioMetroRaw
      : fallback.precio_metro_sin_iva,
    metros_libres: Number.isFinite(metrosLibresRaw)
      ? metrosLibresRaw
      : fallback.metros_libres,
  };
}

/** Lee la config de excedente de fibra cruda; lo que falte toma el valor por defecto. */
function parsearExcedenteFibra(raw: any): ConfigExcedenteFibra {
  const gratisRaw = Number(raw?.metros_gratis);
  const precioRaw = Number(raw?.precio_metro_sin_iva);
  return {
    metros_gratis:
      raw?.metros_gratis != null && Number.isFinite(gratisRaw) && gratisRaw >= 0
        ? gratisRaw
        : DEFAULT_EXCEDENTE_FIBRA_METROS_GRATIS,
    precio_metro_sin_iva:
      raw?.precio_metro_sin_iva != null && Number.isFinite(precioRaw) && precioRaw >= 0
        ? precioRaw
        : DEFAULT_EXCEDENTE_FIBRA_PRECIO_METRO,
  };
}

/** Lee una lista de métodos de pago cruda (strings no vacíos, sin repetidos). */
function parsearMetodos(raw: any): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m: any) => String(m || "").trim())
    .filter((m: string, i: number, a: string[]) => m !== "" && a.indexOf(m) === i);
}

/**
 * Lee el catálogo de conceptos (JSON en un Parametro por slug). Es UN solo
 * catálogo compartido por visitas y traslados; cada concepto, cada método de pago
 * y la config de puntos de red se ligan de forma independiente a cada flujo.
 *
 * Estructura: { iva_porcentaje, conceptos: [ {concepto, valor_sin_iva, liga_producto,
 * permite_punto_red, productos[], aplica_a: ['VISITA'|'TRASLADO']} ],
 * punto_red, punto_red_traslado, metodos_pago, metodos_pago_traslado,
 * excedente_fibra_traslado: { metros_gratis, precio_metro_sin_iva } }.
 * Compatibilidad hacia atrás con { iva_porcentaje, precios: {concepto:{valor_sin_iva}} }
 * y con el mapa plano; y con catálogos sin las claves de traslado (toman las de
 * visita como punto de partida).
 *
 * @param flujo Flujo que consume el catálogo. `conceptos`, `metodosPago` y
 *   `puntoRed` vienen YA filtrados/resueltos para ese flujo; `conceptosTodos`,
 *   `metodosPagoVisita/Traslado` y `puntoRedVisita/Traslado` traen todo (admin).
 */
export function usePreciosConceptosVisita(flujo: FlujoConcepto = "VISITA") {
  // El catálogo ya no es un JSON dentro de Parametro: vive en tablas y el
  // endpoint catalogo-visitas-traslados arma exactamente la misma forma que
  // tenía el "valor" de antes, así que toda la normalización de abajo quedó
  // intacta y las pantallas no notan el cambio.
  const { data, isLoading, isRefetching } = useFetchCatalogoVisitasTraslados();

  // Compat: ya no existe el registro Parametro; el panel guarda por el
  // endpoint nuevo (guardarCatalogoVisitasTraslados).
  const parametro: any = null;

  const parsed: any = (data as any)?.precios_conceptos ?? null;

  const ivaRaw = Number(parsed?.iva_porcentaje);
  const ivaPorcentaje = Number.isFinite(ivaRaw) ? ivaRaw : DEFAULT_IVA_PORCENTAJE;

  // Resolver el array de conceptos según la estructura encontrada.
  let rawConceptos: any[] | null = null;
  if (Array.isArray(parsed?.conceptos)) {
    rawConceptos = parsed.conceptos;
  } else if (parsed?.precios && typeof parsed.precios === "object") {
    // Estructura previa { precios: { concepto: { valor_sin_iva } } }.
    rawConceptos = Object.entries(parsed.precios).map(([concepto, v]: any) => ({
      concepto,
      valor_sin_iva: Number(v?.valor_sin_iva) || 0,
    }));
  } else if (
    parsed &&
    typeof parsed === "object" &&
    !("iva_porcentaje" in parsed) &&
    !("conceptos" in parsed) &&
    !("precios" in parsed)
  ) {
    // Mapa plano más antiguo { concepto: { valor_sin_iva } }.
    rawConceptos = Object.entries(parsed).map(([concepto, v]: any) => ({
      concepto,
      valor_sin_iva: Number(v?.valor_sin_iva) || 0,
    }));
  }

  const conceptosTodos = normalizarConceptos(rawConceptos, ivaPorcentaje);
  const conceptos = conceptosTodos.filter((c) => c.aplica_a.includes(flujo));

  // Mapa concepto→precio (compatibilidad con usos previos que reciben `precios`).
  const precios: Record<string, PrecioConcepto> = {};
  for (const c of conceptos) {
    precios[c.concepto] = {
      valor_sin_iva: c.valor_sin_iva,
      iva: c.iva,
      valor_total: c.valor_total,
    };
  }

  // Config de "Puntos de Red" (cobro del CABLE UTP por metro excedente), por flujo.
  // Traslados: si aún no se configuró, parte de la config de visitas.
  const puntoRedVisita = parsearPuntoRed(parsed?.punto_red, {
    precio_metro_sin_iva: DEFAULT_PUNTO_RED_PRECIO_METRO,
    metros_libres: DEFAULT_PUNTO_RED_METROS_LIBRES,
  });
  const puntoRedTraslado = parsearPuntoRed(parsed?.punto_red_traslado, puntoRedVisita);
  const puntoRed: ConfigPuntoRed = flujo === "TRASLADO" ? puntoRedTraslado : puntoRedVisita;

  // Métodos de pago (parametrizables), por flujo. Traslados: si aún no se
  // configuró su lista, usa la de visitas.
  const _metodosVisita = parsearMetodos(parsed?.metodos_pago);
  const metodosPagoVisita: string[] =
    _metodosVisita.length > 0 ? _metodosVisita : METODOS_PAGO_VISITA_DEFAULT;
  const _metodosTraslado = parsearMetodos(parsed?.metodos_pago_traslado);
  const tieneMetodosTraslado = Array.isArray(parsed?.metodos_pago_traslado);
  const metodosPagoTraslado: string[] = tieneMetodosTraslado && _metodosTraslado.length > 0
    ? _metodosTraslado
    : metodosPagoVisita;
  const metodosPago: string[] = flujo === "TRASLADO" ? metodosPagoTraslado : metodosPagoVisita;

  // Excedente de fibra (solo traslados): metros gratis + precio por metro.
  const excedenteFibraTraslado = parsearExcedenteFibra(parsed?.excedente_fibra_traslado);

  return {
    flujo,
    conceptos,
    conceptosTodos,
    precios,
    ivaPorcentaje,
    puntoRed,
    puntoRedVisita,
    puntoRedTraslado,
    metodosPago,
    metodosPagoVisita,
    metodosPagoTraslado,
    excedenteFibraTraslado,
    parametro,
    isLoading,
    isRefetching,
  };
}
